import { useState } from "react";
import { Alert, Button, Checkbox, SectionCard, Select, TextArea } from "@/deps/ui-library";
import { useInvoiceSetting, useSaveInvoiceSetting } from "@/api/queries";
import type { InvoiceSetting } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { AdditionalChargesCard } from "./AdditionalChargesCard";
import { PaymentOptionsCard } from "./PaymentOptionsCard";

/** The options for a company's invoices. Only what the product acts on is offered here. */
export function InvoicingSetupPage() {
  const { data } = useInvoiceSetting();
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Invoicing setup" />
      {data ? <InvoicingForm saved={data} /> : null}
      <AdditionalChargesCard />
      <PaymentOptionsCard />
    </div>
  );
}

function InvoicingForm({ saved }: { saved: InvoiceSetting }) {
  const say = useTaxWords();
  const save = useSaveInvoiceSetting();
  const [form, setForm] = useState(saved);
  const changed = JSON.stringify(form) !== JSON.stringify(saved);

  return (
    <>
      {save.isError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(save.error as Error).message}
        />
      ) : null}
      <SectionCard title="Invoice options">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Checkbox
              label="HS code column"
              checked={form.showHsCode}
              onCheckedChange={(details) =>
                setForm({ ...form, showHsCode: details.checked === true })
              }
            />
            <p className="text-fg-muted text-xs">
              Shows the HS code field on invoice lines, and the column on printed invoices.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label={say("Show the VAT breakdown on the invoice editor")}
              checked={form.showVatBreakdown}
              onCheckedChange={(details) =>
                setForm({ ...form, showVatBreakdown: details.checked === true })
              }
            />
            <p className="text-fg-muted text-xs">
              The invoice carries the breakdown either way. This only controls whether the editor
              shows it.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Line-level discounts"
              checked={form.lineDiscounts}
              onCheckedChange={(details) =>
                setForm({ ...form, lineDiscounts: details.checked === true })
              }
            />
            <p className="text-fg-muted text-xs">
              A discount and a charge on each line. They take the line&apos;s tax category and
              change its amount.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Document-level discounts"
              checked={form.documentDiscounts}
              onCheckedChange={(details) =>
                setForm({ ...form, documentDiscounts: details.checked === true })
              }
            />
            <p className="text-fg-muted text-xs">
              A discount on the whole invoice, with its own tax category. A charge on the whole
              invoice, such as freight, is always offered.
            </p>
          </div>
          <div className="md:w-1/2">
            <Select
              label="Default sales method"
              options={[
                { value: "credit", label: "Credit invoice" },
                { value: "cash", label: "Cash invoice" },
              ]}
              value={form.cashSaleByDefault ? "cash" : "credit"}
              onChange={(details) => {
                const option = Array.isArray(details.value) ? details.value[0] : details.value;
                if (option) setForm({ ...form, cashSaleByDefault: option.value === "cash" });
              }}
              showClearButton={false}
              helperText="How a new invoice starts. Either can be chosen on the invoice itself."
            />
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Invoice footer">
        <TextArea
          label="Footer text"
          rows={3}
          maxLength={1000}
          value={form.footerText}
          onChange={(event) => setForm({ ...form, footerText: event.target.value })}
          helperText="Printed at the foot of invoices. An invoice keeps the wording it was last saved with, so a change here doesn't alter one already issued."
        />
      </SectionCard>
      <span>
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={!changed || save.isPending}
          onClick={() => save.mutate(form)}
        >
          {save.isPending ? "Saving" : "Save"}
        </Button>
      </span>
    </>
  );
}
